import type { ContentBlockType } from '@eduborg/shared';

/**
 * Concrete payload shapes per block type. Stored as JSON in
 * content_blocks.payload_json — the editor narrows by `block.type`.
 *
 * Schemas live in @eduborg/shared once we have a Phase-2-next-slice need to
 * validate them server-side; the BlockEditor today does its own narrow.
 */
export type BlockPayload =
  | { type: 'text'; text: string }
  | { type: 'heading'; level: 1 | 2 | 3; text: string }
  | { type: 'image'; r2Key: string | null; alt: string; caption: string | null }
  | { type: 'video'; r2Key: string | null; externalUrl: string | null; captionR2Key: string | null }
  | { type: 'embed'; url: string; provider: string | null }
  | { type: 'file'; r2Key: string | null; filename: string; sizeBytes: number }
  | { type: 'document'; html: string }
  | { type: 'freehand'; strokes: Array<{ points: number[] }>; width: number; height: number };

/** Default payload for a freshly-inserted block of the given type. */
export function defaultPayloadFor(type: ContentBlockType): BlockPayload {
  switch (type) {
    case 'text':
      return { type: 'text', text: '' };
    case 'heading':
      return { type: 'heading', level: 2, text: '' };
    case 'image':
      return { type: 'image', r2Key: null, alt: '', caption: null };
    case 'video':
      return { type: 'video', r2Key: null, externalUrl: null, captionR2Key: null };
    case 'embed':
      return { type: 'embed', url: '', provider: null };
    case 'file':
      return { type: 'file', r2Key: null, filename: '', sizeBytes: 0 };
    case 'document':
      return { type: 'document', html: '' };
    case 'freehand':
      return { type: 'freehand', strokes: [], width: 800, height: 400 };
  }
}

/** Parse the persisted payload, with a defensive fallback when the row is malformed. */
export function parsePayload(type: ContentBlockType, raw: string): BlockPayload {
  try {
    const parsed = JSON.parse(raw) as Partial<BlockPayload> & { type?: string };
    if (parsed.type === type) return parsed as BlockPayload;
  } catch {
    /* fall through */
  }
  return defaultPayloadFor(type);
}
